/**
 * Chair-position math for the floor-plan seat rings.
 *
 * Pure functions only — given a table's seat count, shape, and dimensions,
 * return the chair positions in the table's LOCAL coordinate space (origin
 * at the table card's top-left, before any rotation transform).
 *
 * Used by:
 *   - components/tables/TableSeatRing.tsx (visual chair ring)
 *   - components/tables/TableLayoutBuilder.tsx (clamp / padding math)
 */

export type TableShape = 'rectangle' | 'square' | 'round'

export interface ChairPosition {
  /** Chair center X, in table-local px. */
  x: number
  /** Chair center Y, in table-local px. */
  y: number
  /**
   * Chair rotation in degrees. 0 = facing up (back of chair on top edge,
   * seat below). 90 = facing right, 180 = facing down, 270 = facing left.
   */
  angle: number
}

/** A chair's visual footprint along the edge it sits on (px). */
export const CHAIR_FOOTPRINT = 22

/** How far the chair's center sits from the table edge (px). */
export const CHAIR_OFFSET = 14

/** Total padding added by chairs beyond the table's own bounding box. */
export const CHAIR_PAD = CHAIR_OFFSET + Math.ceil(CHAIR_FOOTPRINT / 2)

/**
 * Distribute `seats` chairs around a rectangle. Long sides get filled
 * first; remainder spills to short sides. If the table is too small to
 * accommodate the requested seat count along its perimeter, we tighten
 * the spacing rather than refuse — operators care about "the chairs
 * tracked my seat count" more than perfect spacing.
 */
function rectangleChairs(
  seats: number,
  width: number,
  height: number,
): ChairPosition[] {
  if (seats <= 0) return []

  // Cap how many chairs each side can comfortably hold.
  const topCap = Math.max(1, Math.floor(width / CHAIR_FOOTPRINT))
  const sideCap = Math.max(1, Math.floor(height / CHAIR_FOOTPRINT))

  // Greedy split favoring long sides (top + bottom share, then left + right).
  let top = 0
  let bottom = 0
  let left = 0
  let right = 0

  let remaining = seats
  // Fill top & bottom alternately up to capacity.
  while (remaining > 0 && (top < topCap || bottom < topCap)) {
    if (top <= bottom && top < topCap) {
      top += 1
      remaining -= 1
      continue
    }
    if (bottom < topCap) {
      bottom += 1
      remaining -= 1
    } else {
      break
    }
  }
  // Spill onto sides.
  while (remaining > 0 && (left < sideCap || right < sideCap)) {
    if (left <= right && left < sideCap) {
      left += 1
      remaining -= 1
      continue
    }
    if (right < sideCap) {
      right += 1
      remaining -= 1
    } else {
      break
    }
  }
  // If still leftover, redistribute by adding 1 to each side in turn —
  // accept tighter spacing rather than dropping seats.
  let sideIdx = 0
  while (remaining > 0) {
    if (sideIdx % 4 === 0) top += 1
    else if (sideIdx % 4 === 1) bottom += 1
    else if (sideIdx % 4 === 2) left += 1
    else right += 1
    remaining -= 1
    sideIdx += 1
  }

  const positions: ChairPosition[] = []
  const placeAlong = (
    count: number,
    axisLength: number,
    fixed: number,
    axis: 'x' | 'y',
    angle: number,
  ) => {
    if (count <= 0) return
    // Distribute evenly along the side, with end padding so chairs
    // aren't flush against corners.
    for (let i = 0; i < count; i += 1) {
      const t = (i + 1) / (count + 1) // 0 < t < 1
      const along = t * axisLength
      if (axis === 'x') positions.push({ x: along, y: fixed, angle })
      else positions.push({ x: fixed, y: along, angle })
    }
  }

  placeAlong(top, width, -CHAIR_OFFSET, 'x', 0)
  placeAlong(bottom, width, height + CHAIR_OFFSET, 'x', 180)
  placeAlong(left, height, -CHAIR_OFFSET, 'y', 270)
  placeAlong(right, height, width + CHAIR_OFFSET, 'y', 90)

  return positions
}

/**
 * Distribute `seats` chairs evenly around a round table. The chair's own
 * angle is set so the chair back points outward (away from table center).
 */
function roundChairs(
  seats: number,
  width: number,
  height: number,
): ChairPosition[] {
  if (seats <= 0) return []

  const cx = width / 2
  const cy = height / 2
  const radius = Math.max(width, height) / 2 + CHAIR_OFFSET

  const positions: ChairPosition[] = []
  for (let i = 0; i < seats; i += 1) {
    // Start at the top (-π/2) and walk clockwise.
    const theta = -Math.PI / 2 + (2 * Math.PI * i) / seats
    const x = cx + radius * Math.cos(theta)
    const y = cy + radius * Math.sin(theta)
    // theta = 0 → chair on the right, should face left (270° relative to
    // our "0 = facing up" baseline). Conversion: degrees = theta + 90.
    const angle = ((theta * 180) / Math.PI + 90 + 360) % 360
    positions.push({ x, y, angle })
  }
  return positions
}

/**
 * Public entry point. Returns chair positions for any supported shape.
 * Seat count is clamped to [0, 30] defensively (UI clamps to 1..20, but
 * we don't want a bad DB row to crash the floor render).
 */
export function chairPositions(
  seats: number,
  shape: TableShape,
  width: number,
  height: number,
): ChairPosition[] {
  const s = Math.max(0, Math.min(30, Math.floor(seats)))
  if (s === 0 || !Number.isFinite(width) || !Number.isFinite(height)) return []
  if (width <= 0 || height <= 0) return []

  if (shape === 'round') return roundChairs(s, width, height)
  // Square uses the same distribution as rectangle — the algorithm
  // already balances sides; w == h is a natural case.
  return rectangleChairs(s, width, height)
}
